import {
  HttpErrorResponse,
  HttpInterceptorFn,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  Injector,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { NavigationError, provideRouter, withNavigationErrorHandler } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
  SilentRequest,
} from '@azure/msal-browser';
import {
  APP_VERSION,
  AppConfigService,
  authExpiredInterceptor,
  AuthService,
  correlationIdInterceptor,
  errorInterceptor,
  NavService,
  PERMISSION_SERVICE,
  TranslocoHttpLoader,
} from '@ccfx/shared-lib';
import { provideTransloco, translocoConfig } from '@jsverse/transloco';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';
import packageJson from '../../package.json';
import { routes } from './app.routes';
import { PermissionService } from './features/shared/services/permissions.service';
import { BackofficeNavService } from './features/shared/services/backoffice-nav.service';
import { payloadValueLimitInterceptor } from './interceptors/payload-value-limit.interceptor';

/**
 * Attaches a Bearer token to every request that targets the configured API base URL.
 *
 * This replaces the class-based MsalInterceptor to avoid a bootstrap timing bug:
 * MsalInterceptor caches its protectedResourceMap at construction time via a factory.
 * If Angular constructs MsalInterceptor before provideAppInitializer finishes loading
 * app-config.json, the factory catch-block returns an empty protectedResourceMap and
 * the interceptor silently skips token attachment for the entire session.
 *
 * By calling acquireTokenSilent directly here on each request we eliminate the cached
 * state entirely — the config and active account are always read at request time.
 */
export const msalInterceptorFn: HttpInterceptorFn = (req, next) => {
  const configService = inject(AppConfigService);

  // Skip when config hasn't loaded yet or the request is fetching the config itself
  if (!configService.isLoaded || req.url.includes('/assets/')) {
    return next(req);
  }

  let config;
  try {
    config = configService.getConfig();
  } catch {
    return next(req);
  }

  // Skip in mock/dev mode where there is no real Azure AD session
  if (config.enableMocks) {
    return next(req);
  }

  // Attach tokens only for explicitly configured protected resources.
  const protectedResources: Array<{ baseUrl: string; scopes: string[] }> = [];
  if (config.apiBaseUrl) {
    protectedResources.push({
      baseUrl: config.apiBaseUrl,
      scopes: config.auth.scopes,
    });
  }

  const valguardAutomation = (config as any).valguardAutomation as
    | { apiBaseUrl?: string; scopes?: string[] }
    | undefined;
  if (valguardAutomation?.apiBaseUrl) {
    protectedResources.push({
      baseUrl: valguardAutomation.apiBaseUrl,
      scopes:
        Array.isArray(valguardAutomation.scopes) && valguardAutomation.scopes.length > 0
          ? valguardAutomation.scopes
          : config.auth.scopes,
    });
  }

  const matchedResource = protectedResources.find((r) => req.url.startsWith(r.baseUrl));
  if (!matchedResource) {
    return next(req);
  }

  const msalInstance = inject(MSAL_INSTANCE) as IPublicClientApplication;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  if (!account) {
    // No active session — let the request through; auth-expired-interceptor handles the 401
    return next(req);
  }

  const silentRequest: SilentRequest = { scopes: matchedResource.scopes, account };

  return from(msalInstance.acquireTokenSilent(silentRequest)).pipe(
    switchMap((result) => {
      const authReq = req.clone({
        setHeaders: { Authorization: `Bearer ${result.accessToken}` },
      });
      return next(authReq);
    }),
    catchError((error) => {
      // HTTP errors from next() (e.g. 500, 403) must propagate normally.
      // Only MSAL token acquisition errors (e.g. interaction_in_progress) should be handled here.
      if (error instanceof HttpErrorResponse) {
        return throwError(() => error);
      }
      // Silent acquisition failed — pass through without token so auth-expired-interceptor
      // can attempt a forceRefresh and redirect to login if the session is truly gone
      return next(req);
    }),
  );
};

export function normalizeMsalAuthority(authority: string): string {
  const trimmedAuthority = authority.trim();

  try {
    const parsedAuthority = new URL(trimmedAuthority);
    const pathSegments = parsedAuthority.pathname.split('/').filter(Boolean);

    if (
      parsedAuthority.hostname.toLowerCase() === 'login.microsoftonline.com' &&
      pathSegments.length === 2 &&
      pathSegments[1].toLowerCase() === 'v2.0'
    ) {
      parsedAuthority.pathname = `/${pathSegments[0]}`;
      return parsedAuthority.toString().replace(/\/$/, '');
    }
  } catch {
    return trimmedAuthority;
  }

  return trimmedAuthority;
}

/**
 * SAFE FACTORIES
 * These prevent crashes during bootstrap when the config hasn't loaded yet.
 */
export function MSALInstanceFactory(configService: AppConfigService): PublicClientApplication {
  try {
    const settings = configService.getConfig();
    return new PublicClientApplication({
      auth: {
        clientId: settings.auth.clientId,
        authority: normalizeMsalAuthority(settings.auth.authority),
        redirectUri: settings.auth.redirectUri,
        postLogoutRedirectUri: settings.auth.postLogoutRedirectUri,
      },
      cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
      // Reduce the iframe-based silent token renewal timeout from the 10 s default to
      // 4 s so that when a refresh token is expired the app redirects to login quickly
      // instead of leaving the user staring at a blank/frozen screen.
      system: { iframeBridgeTimeout: 4000 },
    });
  } catch {
    return new PublicClientApplication({
      auth: { clientId: '', authority: 'https://login.microsoftonline.com/common' },
    });
  }
}

export function MSALGuardConfigFactory(configService: AppConfigService): MsalGuardConfiguration {
  try {
    const settings = configService.getConfig();
    return {
      interactionType: InteractionType.Redirect,
      authRequest: { scopes: settings.auth.scopes },
    };
  } catch {
    return { interactionType: InteractionType.Redirect, authRequest: { scopes: [] } };
  }
}

function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withNavigationErrorHandler((error: NavigationError) => {
        if (isChunkLoadError(error.error)) {
          window.location.assign(window.location.href);
        }
      }),
    ),
    provideNativeDateAdapter(),
    provideAppInitializer(() => {
      const configService = inject(AppConfigService);
      const injector = inject(Injector);
      // Load config FIRST, then create AuthService and initialize MSAL,
      // then load user permissions from the API.
      return firstValueFrom(configService.load()).then(async () => {
        await injector.get(AuthService).initialize();
        const authService = injector.get(AuthService);
        if (authService.isAuthenticated) {
          const { UserPermissionStore } =
            await import('./features/shared/services/user-permission.store');
          await injector.get(UserPermissionStore).loadProfile();
        }
      });
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        payloadValueLimitInterceptor,
        correlationIdInterceptor,
        msalInterceptorFn,
        authExpiredInterceptor,
        errorInterceptor,
      ]),
    ),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [AppConfigService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [AppConfigService],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService, // Essential: Fixes NG0201 Error
    { provide: PERMISSION_SERVICE, useExisting: PermissionService },
    { provide: NavService, useClass: BackofficeNavService },
    { provide: APP_VERSION, useValue: packageJson.version },
    provideTransloco({
      config: translocoConfig({
        availableLangs: ['en', 'fr'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          // In production (when prodMode is true), logMissingKey is false and Transloco's default
          // behavior for missing translation keys applies (e.g., returning the key or a fallback).
          logMissingKey: isDevMode(),
        },
      }),
      loader: TranslocoHttpLoader,
    }),
  ],
};
